import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IDynamicFormValue } from 'src/lib';
import { getNextValues } from 'src/components/filters';
import DRAWER_VIEW_TYPE from '../types/drawer-view-type';
import {
  getDrawerData,
  getGridData,
  submitOpportunitiesForProduct,
  resetDrawer,
  submitOpportunitiesForProductsInBulk,
  addBulkCommentsByOpportunity,
  resetAllOpportunities,
  getProductsWithComments,
  getOpportunitySummaryTableData,
  addNewComment,
  deleteCommentById,
  updateComment,
  getAddSKUFormFields,
  getLocalFiltersData,
  getSummaryTableOpportunity,
  getBulkInputsTableOpportunity,
  getBulkInputsTableRows
} from './planner-slice-async-thunks';
import { IOpportunity, IProductComment } from '../types/ppg-sku-drawer.model';
import { isOpportunitySelectionChanged } from '../utils/opportunity-utils';
import { setRowItemOpportunity } from '../utils/bulk-input-utils';
import { IPlannerState } from '../types/planner-state.model';

const initialState: IPlannerState = {
  fetchingMetaData: false,
  fetchingLocalFiltersData: false,
  localFilterFormFields: [],
  localFilterValue: {},
  localFilterInitialValues: {},
  isLocalFiltersFetched: true,
  fetchingGridData: false,
  enableReset: true,
  enableSimulation: true,
  plannerMetaData: {},
  summaryTableOpportunities: {
    ppg: [],
    sku: []
  },
  bulkInputsTableOpportunities: {
    ppg: [],
    sku: []
  },
  toggledView: 'PPG',
  gridLayout: {
    column_headers: [],
    grid_data: [],
    row_headers: []
  },
  isSummaryRefreshRequired: false,
  bulkInputsMetadata: {
    rows: [],
    originalRows: [],
    opportunityTabs: [],
    isAddingComment: false,
    isApplyingOpportunities: false,
    isOpportunitySelectionChanged: false
  },
  productsWithComments: [],
  drawerViewMetadata: {
    isDrawerOpen: false,
    viewType: null,
    productId: null,
    isLoading: false,
    ppgId: null,
    product: {
      opportunities: {
        ppg: [],
        sku: []
      },
      parent_details: null,
      child_details: []
    },

    opportunitySelectionMap: [],
    initialChildOpportunities: [],
    initialParentOpportunities: [],
    isSubmittingDrawerData: false
  },
  isCommentLoading: false,
  isFetchingOpportunitySummaryData: false,
  opportunitySummaryDetails: [],
  formFields: [],
  addSKUFieldCombinations: [],
  isFetchingFormFields: false,
  isFetchingBulkInputs: false
};

/* eslint-disable no-param-reassign */
const plannerSlice = createSlice({
  name: 'planner',
  initialState,
  reducers: {
    resetBulkInputsTableRows(state) {
      state.bulkInputsMetadata.rows = [];
      state.bulkInputsMetadata.isOpportunitySelectionChanged = false;
    },
    setOpportunities(
      state,
      action: PayloadAction<{
        parentIndex: number;
        childIndex: number;
        newOpportunities: number[];
        changedOpportunity: IOpportunity;
      }>
    ) {
      setRowItemOpportunity(state, action.payload);
      /**
       * Computing if the selection has changed
       */
      state.bulkInputsMetadata.isOpportunitySelectionChanged =
        state.bulkInputsMetadata.rows.some((row, index) =>
          isOpportunitySelectionChanged(
            state.bulkInputsMetadata.originalRows[index].opportunities,
            state.bulkInputsMetadata.originalRows[index].subRows.map(
              (subRow) => subRow.opportunities
            ),
            row.opportunities,
            row.subRows.map((subRow) => subRow.opportunities)
          )
        );
    },
    setAllOpportunities(
      state,
      action: PayloadAction<{
        changedOpportunity: IOpportunity;
        isOpportunityRemoved: boolean;
      }>
    ) {
      const { changedOpportunity, isOpportunityRemoved } = action.payload;
      state.bulkInputsMetadata.rows.forEach((row) => {
        const newOpportunitiesForParentItem = row.opportunities
          .filter((opportunity) => {
            if (isOpportunityRemoved) {
              return opportunity !== changedOpportunity.pk_id;
            }
            // If the opportunity is added and it is supposed to disable any of the already checked opportunity
            // then we remove the checked opportunity
            return !changedOpportunity.disables_opportunity.includes(
              opportunity
            );
          })
          .slice();
        if (
          !isOpportunityRemoved &&
          !newOpportunitiesForParentItem.includes(changedOpportunity.pk_id)
        ) {
          newOpportunitiesForParentItem.push(changedOpportunity.pk_id);
        }
        setRowItemOpportunity(state, {
          changedOpportunity,
          childIndex: null,
          newOpportunities: newOpportunitiesForParentItem,
          parentIndex: row.itemIndex
        });
        row.subRows.forEach((subRow) => {
          const newOpportunitiesForChildItem = subRow.opportunities
            .filter((opportunity) => {
              if (isOpportunityRemoved) {
                return opportunity !== changedOpportunity.pk_id;
              }
              // If the opportunity is added and it is supposed to disable any of the already checked opportunity
              // then we remove the checked opportunity
              return !changedOpportunity.disables_opportunity.includes(
                opportunity
              );
            })
            .slice();
          if (!isOpportunityRemoved) {
            if (
              !newOpportunitiesForChildItem.includes(changedOpportunity.pk_id)
            ) {
              newOpportunitiesForChildItem.push(changedOpportunity.pk_id);
            }
          }
          setRowItemOpportunity(state, {
            changedOpportunity,
            childIndex: subRow.itemIndex,
            newOpportunities: newOpportunitiesForChildItem,
            parentIndex: row.itemIndex
          });
        });
      });
      /**
       * Computing if the selection has changed
       */
      state.bulkInputsMetadata.isOpportunitySelectionChanged =
        state.bulkInputsMetadata.rows.some((row, index) =>
          isOpportunitySelectionChanged(
            state.bulkInputsMetadata.originalRows[index].opportunities,
            state.bulkInputsMetadata.originalRows[index].subRows.map(
              (subRow) => subRow.opportunities
            ),
            row.opportunities,
            row.subRows.map((subRow) => subRow.opportunities)
          )
        );
    },
    togglePlannerGridView(state, action: PayloadAction<string>) {
      state.toggledView = action.payload;
    },
    setLocalFilterValue(state, action: PayloadAction<IDynamicFormValue>) {
      state.localFilterValue = action.payload;
    },
    toggleDrawerView(
      state,
      action: PayloadAction<{
        view: DRAWER_VIEW_TYPE;
        productId: number;
        ppgId: number;
      }>
    ) {
      state.drawerViewMetadata.isDrawerOpen =
        !state.drawerViewMetadata.isDrawerOpen;
      const { productId, view, ppgId } = action.payload;
      state.drawerViewMetadata.viewType = view;
      state.drawerViewMetadata.productId = productId;
      state.drawerViewMetadata.ppgId = ppgId;
    },
    addCommentForCommentDrawerParentItem(
      state,
      action: PayloadAction<{ comment: IProductComment; productIndex: number }>
    ) {
      const { comment, productIndex } = action.payload;
      const product = state.productsWithComments[productIndex];
      product.comments.unshift(comment);
      state.isSummaryRefreshRequired = true;
    },
    addCommentForCommentDrawerChildItem(
      state,
      action: PayloadAction<{
        comment: IProductComment;
        parentIndex: number;
        childIndex: number;
      }>
    ) {
      const { comment, parentIndex, childIndex } = action.payload;
      const product = state.productsWithComments[parentIndex];
      const child = product.children[childIndex];
      child.comments.unshift(comment);
      state.isSummaryRefreshRequired = true;
    },
    updateCommentForCommentDrawerParentItem(
      state,
      action: PayloadAction<{
        comment: IProductComment;
        productIndex: number;
      }>
    ) {
      const { comment, productIndex } = action.payload;
      const product = state.productsWithComments[productIndex];
      const commentIndex = product.comments.findIndex(
        (childComment) => childComment.pk_id === comment.pk_id
      );
      if (commentIndex > -1) {
        product.comments[commentIndex] = comment;
      }
      state.isSummaryRefreshRequired = true;
    },
    updateCommentForCommentDrawerChildItem(
      state,
      action: PayloadAction<{
        comment: IProductComment;
        parentIndex: number;
        childIndex: number;
      }>
    ) {
      const { comment, parentIndex, childIndex } = action.payload;
      const product = state.productsWithComments[parentIndex];
      const child = product.children[childIndex];
      const commentIndex = child.comments.findIndex(
        (childComment) => childComment.pk_id === comment.pk_id
      );
      if (commentIndex > -1) {
        child.comments[commentIndex] = comment;
      }
      state.isSummaryRefreshRequired = true;
    },
    deleteCommentForCommentDrawerParentItem(
      state,
      action: PayloadAction<{
        commentId: number;
        productIndex: number;
      }>
    ) {
      const { commentId, productIndex } = action.payload;
      const product = state.productsWithComments[productIndex];
      const commentIndex = product.comments.findIndex(
        (childComment) => childComment.pk_id === commentId
      );
      if (commentIndex > -1) {
        product.comments.splice(commentIndex, 1);
      }
    },
    deleteCommentForCommentDrawerChildItem(
      state,
      action: PayloadAction<{
        commentId: number;
        parentIndex: number;
        childIndex: number;
      }>
    ) {
      const { commentId, parentIndex, childIndex } = action.payload;
      const product = state.productsWithComments[parentIndex];
      const child = product.children[childIndex];
      const commentIndex = child.comments.findIndex(
        (childComment) => childComment.pk_id === commentId
      );
      if (commentIndex > -1) {
        child.comments.splice(commentIndex, 1);
      }
    },
    toggleDrawerParentItemOpportunity(
      state,
      action: PayloadAction<{
        selectedOpportunities: number[];
        changedOpportunity: IOpportunity;
        isOpportunityChecked: boolean;
      }>
    ) {
      const {
        selectedOpportunities,
        changedOpportunity,
        isOpportunityChecked
      } = action.payload;
      if (isOpportunityChecked) {
        state.drawerViewMetadata.product.parent_details.selected_opportunities =
          selectedOpportunities.filter(
            (opportunityId) =>
              !changedOpportunity.disables_opportunity.includes(opportunityId)
          );
        state.drawerViewMetadata.product.child_details.forEach((child) => {
          if (
            !child.selected_opportunities.includes(changedOpportunity.pk_id)
          ) {
            child.selected_opportunities.push(changedOpportunity.pk_id);
          }
          /**
           * Removing any of the opportunities in child that should be removed on check of current opportunity
           */
          child.selected_opportunities = child.selected_opportunities.filter(
            (opportunityId) =>
              !changedOpportunity.disables_opportunity.includes(opportunityId)
          );
        });
      } else {
        state.drawerViewMetadata.product.parent_details.selected_opportunities =
          selectedOpportunities;
        state.drawerViewMetadata.product.child_details.forEach((child) => {
          if (child.selected_opportunities.includes(changedOpportunity.pk_id)) {
            child.selected_opportunities.splice(
              child.selected_opportunities.indexOf(changedOpportunity.pk_id),
              1
            );
          }
        });
      }
    },
    toggleDrawerChildItemOpportunity(
      state,
      action: PayloadAction<{
        selectedOpportunities: number[];
        changedOpportunity: IOpportunity;
        isOpportunityChecked: boolean;
        indexOfChild: number;
      }>
    ) {
      const {
        selectedOpportunities,
        changedOpportunity,
        indexOfChild,
        isOpportunityChecked
      } = action.payload;
      const child =
        state.drawerViewMetadata.product.child_details[indexOfChild];
      if (isOpportunityChecked) {
        child.selected_opportunities = selectedOpportunities.filter(
          (opportunityId) =>
            !changedOpportunity.disables_opportunity.includes(opportunityId)
        );
      } else {
        child.selected_opportunities = selectedOpportunities;
      }
      state.drawerViewMetadata.product.parent_details.selected_opportunities =
        state.drawerViewMetadata.product.opportunities.ppg
          .map((opportunity) => {
            const childrenWithParentOpportunityId =
              state.drawerViewMetadata.product.child_details.filter(
                (childProduct) =>
                  childProduct.selected_opportunities.includes(
                    opportunity.pk_id
                  )
              );
            if (!childrenWithParentOpportunityId.length) {
              return null;
            }
            if (
              childrenWithParentOpportunityId.length ===
              state.drawerViewMetadata.product.child_details.length
            ) {
              return opportunity.pk_id;
            }
            return opportunity.intermediate_opp_id;
          })
          .filter((opportunityId) => opportunityId !== null);
    },
    addNewCommentForProductDrawerParentItem(
      state,
      action: PayloadAction<IProductComment>
    ) {
      state.drawerViewMetadata.product.parent_details.comments.unshift(
        action.payload
      );
      state.isSummaryRefreshRequired = true;
    },
    addNewCommentForProductDrawerChildItem(
      state,
      action: PayloadAction<{ commentIndex: number; comment: IProductComment }>
    ) {
      const { commentIndex, comment } = action.payload;
      const childItem =
        state.drawerViewMetadata.product.child_details[commentIndex];
      childItem.comments.unshift(comment);
      state.isSummaryRefreshRequired = true;
    },
    updateCommentForProductDrawerParentItem(
      state,
      action: PayloadAction<IProductComment>
    ) {
      const comment = action.payload;
      const commentIndex =
        state.drawerViewMetadata.product.parent_details.comments.findIndex(
          (parentComment) => parentComment.pk_id === comment.pk_id
        );
      if (commentIndex > -1) {
        state.drawerViewMetadata.product.parent_details.comments[commentIndex] =
          comment;
      }
      state.isSummaryRefreshRequired = true;
    },
    updateCommentForProductDrawerChildItem(
      state,
      action: PayloadAction<{ comment: IProductComment; itemIndex: number }>
    ) {
      const { comment, itemIndex } = action.payload;
      const childItem =
        state.drawerViewMetadata.product.child_details[itemIndex];
      const commentIndex = childItem.comments.findIndex(
        (childComment) => childComment.pk_id === comment.pk_id
      );
      if (commentIndex > -1) {
        childItem[commentIndex] = comment;
      }
      state.isSummaryRefreshRequired = true;
    },
    deleteCommentFromProductDrawerParentItem(
      state,
      action: PayloadAction<number>
    ) {
      const commentId = action.payload;
      const commentIndex =
        state.drawerViewMetadata.product.parent_details.comments.findIndex(
          (parentComment) => parentComment.pk_id === commentId
        );
      if (commentIndex > -1) {
        state.drawerViewMetadata.product.parent_details.comments.splice(
          commentIndex,
          1
        );
      }
      state.isSummaryRefreshRequired = true;
    },
    deleteCommentFromProductDrawerChildItem(
      state,
      action: PayloadAction<{ commentId: number; itemIndex: number }>
    ) {
      const { commentId, itemIndex } = action.payload;
      const childItem =
        state.drawerViewMetadata.product.child_details[itemIndex];
      const commentIndex = childItem.comments.findIndex(
        (childComment) => childComment.pk_id === commentId
      );
      if (commentIndex > -1) {
        childItem.comments.splice(commentIndex, 1);
      }
      state.isSummaryRefreshRequired = true;
    },
    resetGridMetrics(state) {
      state.localFilterInitialValues =
        state.localFilterFormFields.reduce<IDynamicFormValue>(
          (prev, curr) => ({ ...prev, [curr.name]: curr.default_value }),
          {}
        );
    },
    //  handler for enabling/disabling create sku form fields as per toggle value
    toggleDisableStateForSkuFormFields(
      state,
      action: PayloadAction<{
        shouldEnableAutoFillFields: boolean;
      }>
    ) {
      const { shouldEnableAutoFillFields } = action.payload;
      state.formFields
        .filter((e) => e.is_auto_fill)
        .forEach((ele) => {
          ele.is_active = shouldEnableAutoFillFields;
        });
    },

    setSkuFormFieldOptions(
      state,
      action: PayloadAction<
        ReturnType<typeof getNextValues>['fieldNameToOptionsMap']
      >
    ) {
      const fieldNameToOptionsMap = action.payload;
      state.formFields = state.formFields.map((filter) => {
        if (fieldNameToOptionsMap[filter.name]) {
          return { ...filter, values: fieldNameToOptionsMap[filter.name] };
        }
        return filter;
      });
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getGridData.pending, (state) => {
        state.drawerViewMetadata = {
          isDrawerOpen: false,
          viewType: null,
          productId: null,
          ppgId: null,
          isLoading: false,
          product: {
            opportunities: {
              ppg: [],
              sku: []
            },
            parent_details: null,
            child_details: []
          },
          opportunitySelectionMap: [],
          initialChildOpportunities: [],
          initialParentOpportunities: [],
          isSubmittingDrawerData: false
        };
        state.fetchingGridData = true;
      })
      .addCase(getGridData.fulfilled, (state, action) => {
        state.fetchingGridData = false;
        state.gridLayout = action.payload;
      })
      .addCase(getGridData.rejected, (state) => {
        state.fetchingGridData = false;
      })
      .addCase(getLocalFiltersData.pending, (state) => {
        state.fetchingLocalFiltersData = true;
      })
      .addCase(getLocalFiltersData.fulfilled, (state, action) => {
        state.fetchingLocalFiltersData = false;
        const { fields, values } = action.payload;
        state.localFilterFormFields = fields;
        state.localFilterInitialValues = values;
        state.localFilterValue = values;
      })
      .addCase(getLocalFiltersData.rejected, (state) => {
        state.fetchingLocalFiltersData = false;
      })
      .addCase(getDrawerData.pending, (state) => {
        state.drawerViewMetadata.isLoading = true;
      })
      .addCase(getDrawerData.rejected, (state) => {
        state.drawerViewMetadata.isLoading = false;
      })
      .addCase(getDrawerData.fulfilled, (state, action) => {
        state.drawerViewMetadata.isLoading = false;
        state.drawerViewMetadata.product = action.payload;
        state.drawerViewMetadata.initialParentOpportunities =
          action.payload.parent_details.selected_opportunities.slice();
        state.drawerViewMetadata.initialChildOpportunities =
          action.payload.child_details.map((child) =>
            child.selected_opportunities.slice()
          );
      })
      .addCase(submitOpportunitiesForProduct.pending, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = true;
      })
      .addCase(submitOpportunitiesForProduct.rejected, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = false;
      })
      .addCase(submitOpportunitiesForProduct.fulfilled, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = false;
      })
      .addCase(submitOpportunitiesForProductsInBulk.pending, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = true;
      })
      .addCase(submitOpportunitiesForProductsInBulk.rejected, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
      })
      .addCase(submitOpportunitiesForProductsInBulk.fulfilled, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
      })
      .addCase(resetAllOpportunities.pending, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = true;
      })
      .addCase(resetAllOpportunities.rejected, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
      })
      .addCase(resetAllOpportunities.fulfilled, (state, action) => {
        const { resetAll } = action.payload;
        state.bulkInputsMetadata.isApplyingOpportunities = false;

        state.bulkInputsMetadata.rows.forEach((row) => {
          row.opportunities = [];
          row.subRows.forEach((subRow) => {
            subRow.opportunities = [];
          });
        });
        state.gridLayout.grid_data.forEach((row) => {
          row.forEach((cell) => {
            cell.ppg_toggle.forEach((ppg) => {
              ppg.opportunity = [];
            });
            cell.sku_toggle.forEach((ppgGroupedBySKU) => {
              ppgGroupedBySKU.opportunity = [];
              ppgGroupedBySKU.skus.forEach((sku) => {
                sku.opportunity = [];
              });
            });
          });
        });

        state.isSummaryRefreshRequired = true;
      })
      .addCase(resetDrawer.pending, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = true;
      })
      .addCase(resetDrawer.rejected, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = false;
      })
      .addCase(resetDrawer.fulfilled, (state, action) => {
        state.drawerViewMetadata.isSubmittingDrawerData = false;
        const { resetAll } = action.payload;
        const { child_details: childDetails, parent_details: parentDetails } =
          state.drawerViewMetadata.product;
        state.drawerViewMetadata.isSubmittingDrawerData = false;
        parentDetails.selected_opportunities = [];
        if (resetAll) {
          state.drawerViewMetadata.product.parent_details.comments = [];
        }
        childDetails.forEach((child) => {
          if (resetAll) {
            // eslint-disable-next-line no-param-reassign
            child.comments = [];
          }
          // eslint-disable-next-line no-param-reassign
          child.selected_opportunities = [];
        });
        state.isSummaryRefreshRequired = true;
      })
      .addCase(getSummaryTableOpportunity.pending, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = true;
      })
      .addCase(getSummaryTableOpportunity.rejected, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
      })
      .addCase(getSummaryTableOpportunity.fulfilled, (state, action) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
        state.summaryTableOpportunities = action.payload;
      })
      .addCase(getBulkInputsTableOpportunity.pending, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = true;
      })
      .addCase(getBulkInputsTableOpportunity.rejected, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
      })
      .addCase(getBulkInputsTableOpportunity.fulfilled, (state, action) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
        state.bulkInputsTableOpportunities = action.payload;
      })
      .addCase(addBulkCommentsByOpportunity.pending, (state) => {
        state.bulkInputsMetadata.isAddingComment = true;
      })
      .addCase(addBulkCommentsByOpportunity.rejected, (state) => {
        state.bulkInputsMetadata.isAddingComment = false;
      })
      .addCase(addBulkCommentsByOpportunity.fulfilled, (state) => {
        state.bulkInputsMetadata.isAddingComment = false;
        state.isSummaryRefreshRequired = true;
      })
      .addCase(getProductsWithComments.pending, (state) => {
        state.drawerViewMetadata.isLoading = true;
      })
      .addCase(getProductsWithComments.rejected, (state) => {
        state.drawerViewMetadata.isLoading = false;
      })
      .addCase(getProductsWithComments.fulfilled, (state, action) => {
        state.drawerViewMetadata.isLoading = false;
        state.productsWithComments = action.payload;
      })
      .addCase(getOpportunitySummaryTableData.pending, (state) => {
        state.isFetchingOpportunitySummaryData = true;
      })
      .addCase(getOpportunitySummaryTableData.rejected, (state) => {
        state.isFetchingOpportunitySummaryData = false;
      })
      .addCase(getOpportunitySummaryTableData.fulfilled, (state, action) => {
        state.isFetchingOpportunitySummaryData = false;
        state.isSummaryRefreshRequired = false;
        state.opportunitySummaryDetails = action.payload;
      })
      .addCase(addNewComment.fulfilled, (state) => {
        state.isSummaryRefreshRequired = true;
      })
      .addCase(updateComment.fulfilled, (state) => {
        state.isSummaryRefreshRequired = true;
      })
      .addCase(deleteCommentById.fulfilled, (state) => {
        state.isSummaryRefreshRequired = true;
      })
      .addCase(getAddSKUFormFields.pending, (state) => {
        state.isFetchingFormFields = true;
      })
      .addCase(getAddSKUFormFields.rejected, (state) => {
        state.isFetchingFormFields = false;
      })
      .addCase(getAddSKUFormFields.fulfilled, (state, action) => {
        state.isFetchingFormFields = false;
        const { combinations, fields } = action.payload;
        state.formFields = fields;
        state.addSKUFieldCombinations = combinations;
      })
      .addCase(getBulkInputsTableRows.pending, (state) => {
        state.isFetchingBulkInputs = true;
        state.bulkInputsMetadata.rows = [];
        state.bulkInputsMetadata.originalRows = [];
      })
      .addCase(getBulkInputsTableRows.rejected, (state) => {
        state.isFetchingBulkInputs = false;
        state.bulkInputsMetadata.rows = [];
        state.bulkInputsMetadata.originalRows = [];
      })
      .addCase(getBulkInputsTableRows.fulfilled, (state, action) => {
        state.isFetchingBulkInputs = false;
        state.bulkInputsMetadata.rows = action.payload;
        state.bulkInputsMetadata.originalRows = action.payload;
      })
});
/* eslint-enable no-param-reassign */

export const {
  setLocalFilterValue,
  togglePlannerGridView,
  toggleDrawerView,
  resetGridMetrics,
  toggleDrawerChildItemOpportunity,
  toggleDrawerParentItemOpportunity,
  resetBulkInputsTableRows,
  setOpportunities,
  setAllOpportunities,
  addNewCommentForProductDrawerParentItem,
  addNewCommentForProductDrawerChildItem,
  deleteCommentFromProductDrawerChildItem,
  deleteCommentFromProductDrawerParentItem,
  updateCommentForProductDrawerChildItem,
  updateCommentForProductDrawerParentItem,
  addCommentForCommentDrawerChildItem,
  addCommentForCommentDrawerParentItem,
  deleteCommentForCommentDrawerChildItem,
  deleteCommentForCommentDrawerParentItem,
  updateCommentForCommentDrawerChildItem,
  updateCommentForCommentDrawerParentItem,
  setSkuFormFieldOptions,
  toggleDisableStateForSkuFormFields
} = plannerSlice.actions;

export default plannerSlice;
